import { LayerType } from "../Player/LayerType";

/**
 * Enthaelt die Presentation echte 3D-Inhalte (Modelle, Primitive, 3D-Text,
 * 3D-Diagramme, Effekte)? Dann schaltet der Reader die Kantenglaettung (MSAA)
 * ein — sie ist ein Parameter der WebGL-Kontext-Erzeugung und laesst sich
 * spaeter nicht mehr aendern.
 *
 * Hintergrund (Jans Fund 19.09.2026, „sehr pixelige Kanten auf dem iPhone"):
 * Der Editor erzwingt MSAA, der Reader liess sie aus — der Store-Schalter
 * `antialiasing` steht dort auf false und wird von niemandem gesetzt (die
 * Checkbox im 3D-WELT-Reiter ist eine Editor-Sitzungs-Einstellung, sie wird
 * nicht ins Dokument geschrieben). Reine Bild-/Text-Scrollys bleiben ohne
 * MSAA: ihre Flaechen stehen achsparallel, dort gibt es nichts zu glaetten.
 */
const DREI_D_TYPEN: ReadonlySet<string> = new Set([
    LayerType.object3d,
    LayerType.object3dChild,
    LayerType.infographic,
    LayerType.effect,
]);

interface EbeneLike {
    type?: string;
    layers?: unknown;
}

export function hat3dEbenen(groupTree: unknown): boolean {
    const stapel: unknown[] = Array.isArray(groupTree) ? [...groupTree] : [groupTree];
    let schritte = 0;
    while (stapel.length > 0 && schritte < 20000) {
        schritte += 1;
        const ebene = stapel.pop() as EbeneLike | null | undefined;
        if (!ebene || typeof ebene !== "object") continue;
        if (typeof ebene.type === "string" && DREI_D_TYPEN.has(ebene.type)) return true;
        if (Array.isArray(ebene.layers)) stapel.push(...ebene.layers);
    }
    return false;
}

/**
 * Traegt eine Ebene eine Drehung (fester Winkel, Dreh-Keyframes oder
 * Eigenbewegung „Drehen")? Dann stehen auch flache Bilder schraeg — ihre
 * Kanten brauchen die Kantenglaettung genauso wie 3D-Inhalte.
 *
 * Jans Fund 29.09.2026 (Template „in Motion" am iPhone): drehende Bilder mit
 * Treppenkanten. Die Annahme oben „Bild-Scrollys stehen achsparallel" galt
 * nicht mehr, seit Ebenen sich drehen koennen.
 */
const SCHRAEG = 0.001;
const istSchraeg = (wert: unknown): boolean =>
    Array.isArray(wert)
        ? wert.some((v) => typeof v === "number" && Math.abs(v) > SCHRAEG)
        : typeof wert === "number" && Math.abs(wert) > SCHRAEG;

interface DrehEbeneLike extends EbeneLike {
    animationsGroups?: { rotation?: { initialValue?: unknown; active?: boolean; keyframes?: Array<{ value?: unknown }> } };
    loops?: { rotation?: { active?: boolean } };
}

export function hatGedrehteEbenen(groupTree: unknown): boolean {
    const stapel: unknown[] = Array.isArray(groupTree) ? [...groupTree] : [groupTree];
    let schritte = 0;
    while (stapel.length > 0 && schritte < 20000) {
        schritte += 1;
        const ebene = stapel.pop() as DrehEbeneLike | null | undefined;
        if (!ebene || typeof ebene !== "object") continue;
        if (ebene.loops?.rotation?.active) return true;
        const dreh = ebene.animationsGroups?.rotation;
        if (dreh) {
            if (istSchraeg(dreh.initialValue)) return true;
            if (dreh.active && dreh.keyframes?.some((k) => istSchraeg(k.value))) return true;
        }
        if (Array.isArray(ebene.layers)) stapel.push(...ebene.layers);
    }
    return false;
}
